import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  Plus, Trash2, TrendingUp, TrendingDown, Minus, Scale, Activity, 
  Heart, Dumbbell, Loader2, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PhysioHistoryChart = ({ clientId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newRecord, setNewRecord] = useState({
    weight: '',
    muscle_mass: '',
    body_fat: '',
    visceral_fat: '',
    measured_at: new Date().toISOString().split('T')[0]
  });

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/client/${clientId}/physio-history`);
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const addRecord = async () => {
    if (!newRecord.weight && !newRecord.muscle_mass && !newRecord.body_fat) {
      toast.error('Introduce al menos un valor');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/admin/client/${clientId}/physio-history`, {
        weight: newRecord.weight ? parseFloat(newRecord.weight) : null,
        muscle_mass: newRecord.muscle_mass ? parseFloat(newRecord.muscle_mass) : null,
        body_fat: newRecord.body_fat ? parseFloat(newRecord.body_fat) : null,
        visceral_fat: newRecord.visceral_fat ? parseFloat(newRecord.visceral_fat) : null,
        measured_at: newRecord.measured_at
      });
      toast.success('Registro añadido');
      setShowAddDialog(false);
      setNewRecord({
        weight: '',
        muscle_mass: '',
        body_fat: '',
        visceral_fat: '',
        measured_at: new Date().toISOString().split('T')[0]
      });
      fetchHistory();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const deleteRecord = async (recordId) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    
    try {
      await axios.delete(`${API}/admin/client/${clientId}/physio-history/${recordId}`);
      toast.success('Registro eliminado');
      fetchHistory();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Calcular tendencias
  const getTrend = (field) => {
    if (history.length < 2) return null;
    const recent = history[history.length - 1][field];
    const previous = history[history.length - 2][field];
    if (!recent || !previous) return null;
    const diff = recent - previous;
    return { diff: diff.toFixed(1), isUp: diff > 0, isEqual: diff === 0 };
  };

  const weightTrend = getTrend('weight');
  const muscleTrend = getTrend('muscle_mass');
  const fatTrend = getTrend('body_fat');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          Historial de Mediciones
        </h3>
        <Button onClick={() => setShowAddDialog(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Medición
        </Button>
      </div>

      {/* Trend Cards */}
      {history.length >= 2 && (
        <div className="grid grid-cols-3 gap-4">
          {/* Weight Trend */}
          <Card className={`border ${weightTrend?.isUp ? 'border-red-500/30 bg-red-500/5' : weightTrend?.diff < 0 ? 'border-green-500/30 bg-green-500/5' : 'border-border'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Scale className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Peso</span>
                </div>
                {weightTrend && (
                  <div className={`flex items-center gap-1 ${weightTrend.isUp ? 'text-red-400' : 'text-green-400'}`}>
                    {weightTrend.isUp ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                    <span className="text-sm font-bold">{weightTrend.isUp ? '+' : ''}{weightTrend.diff} kg</span>
                  </div>
                )}
              </div>
              <p className="text-2xl font-bold text-foreground mt-1">
                {history[history.length - 1]?.weight || '-'} kg
              </p>
            </CardContent>
          </Card>

          {/* Muscle Mass Trend */}
          <Card className={`border ${muscleTrend?.isUp ? 'border-green-500/30 bg-green-500/5' : muscleTrend?.diff < 0 ? 'border-red-500/30 bg-red-500/5' : 'border-border'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Dumbbell className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Músculo</span>
                </div>
                {muscleTrend && (
                  <div className={`flex items-center gap-1 ${muscleTrend.isUp ? 'text-green-400' : 'text-red-400'}`}>
                    {muscleTrend.isUp ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                    <span className="text-sm font-bold">{muscleTrend.isUp ? '+' : ''}{muscleTrend.diff}%</span>
                  </div>
                )}
              </div>
              <p className="text-2xl font-bold text-foreground mt-1">
                {history[history.length - 1]?.muscle_mass || '-'}%
              </p>
            </CardContent>
          </Card>

          {/* Body Fat Trend */}
          <Card className={`border ${fatTrend?.isUp ? 'border-red-500/30 bg-red-500/5' : fatTrend?.diff < 0 ? 'border-green-500/30 bg-green-500/5' : 'border-border'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Heart className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Grasa</span>
                </div>
                {fatTrend && (
                  <div className={`flex items-center gap-1 ${fatTrend.isUp ? 'text-red-400' : 'text-green-400'}`}>
                    {fatTrend.isUp ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                    <span className="text-sm font-bold">{fatTrend.isUp ? '+' : ''}{fatTrend.diff}%</span>
                  </div>
                )}
              </div>
              <p className="text-2xl font-bold text-foreground mt-1">
                {history[history.length - 1]?.body_fat || '-'}%
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Chart */}
      {history.length > 0 ? (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base">Evolución en el Tiempo</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis 
                  dataKey="measured_at" 
                  stroke="#888"
                  tick={{ fill: '#888', fontSize: 12 }}
                />
                <YAxis stroke="#888" tick={{ fill: '#888', fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1a1a1a', 
                    border: '1px solid #333',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="weight" 
                  name="Peso (kg)" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="muscle_mass" 
                  name="Músculo (%)" 
                  stroke="#22c55e" 
                  strokeWidth={2}
                  dot={{ fill: '#22c55e' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="body_fat" 
                  name="Grasa (%)" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  dot={{ fill: '#ef4444' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center">
            <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No hay mediciones registradas</p>
            <p className="text-sm text-muted-foreground mt-1">Añade la primera medición para ver el historial</p>
          </CardContent>
        </Card>
      )}

      {/* History Table */}
      {history.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base">Registros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-2 text-muted-foreground">Fecha</th>
                    <th className="text-right p-2 text-muted-foreground">Peso</th>
                    <th className="text-right p-2 text-muted-foreground">Músculo</th>
                    <th className="text-right p-2 text-muted-foreground">Grasa</th>
                    <th className="text-right p-2 text-muted-foreground">G. Visceral</th>
                    <th className="text-right p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {[...history].reverse().map((record, idx) => (
                    <tr key={record.id} className="border-b border-border/50 hover:bg-zinc-800/30">
                      <td className="p-2 text-foreground">{record.measured_at}</td>
                      <td className="p-2 text-right text-foreground">{record.weight ? `${record.weight} kg` : '-'}</td>
                      <td className="p-2 text-right text-foreground">{record.muscle_mass ? `${record.muscle_mass}%` : '-'}</td>
                      <td className="p-2 text-right text-foreground">{record.body_fat ? `${record.body_fat}%` : '-'}</td>
                      <td className="p-2 text-right text-foreground">{record.visceral_fat || '-'}</td>
                      <td className="p-2 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteRecord(record.id)}
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Record Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Nueva Medición
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Fecha de medición</Label>
              <Input
                type="date"
                value={newRecord.measured_at}
                onChange={(e) => setNewRecord({...newRecord, measured_at: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Peso (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="78.5"
                  value={newRecord.weight}
                  onChange={(e) => setNewRecord({...newRecord, weight: e.target.value})}
                />
              </div>
              <div>
                <Label>Masa Muscular (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="35.2"
                  value={newRecord.muscle_mass}
                  onChange={(e) => setNewRecord({...newRecord, muscle_mass: e.target.value})}
                />
              </div>
              <div>
                <Label>Grasa Corporal (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="22.5"
                  value={newRecord.body_fat}
                  onChange={(e) => setNewRecord({...newRecord, body_fat: e.target.value})}
                />
              </div>
              <div>
                <Label>Grasa Visceral (índice)</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="8"
                  value={newRecord.visceral_fat}
                  onChange={(e) => setNewRecord({...newRecord, visceral_fat: e.target.value})}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={addRecord} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhysioHistoryChart;
